// Pixel particles (sparks, droplets, plankton motes) and floating numbers.
// Purely presentational; nothing here feeds back into the simulation.
import * as THREE from 'three';

const MAX = 2400;
const vs = /* glsl */`
attribute float size; attribute vec4 tint;
varying vec4 vTint;
uniform float scale;
void main() {
  vTint = tint;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = max(1.0, size * scale / -mv.z);
  gl_Position = projectionMatrix * mv;
}`;
const fs = /* glsl */`
varying vec4 vTint;
void main() { if (vTint.a <= 0.0) discard; float d = length(gl_PointCoord - 0.5); if (d > 0.5) discard; gl_FragColor = vec4(vTint.rgb * vTint.a * smoothstep(0.5, 0.2, d), 1.0); }`;   // round motes, never squares

export class Fx {
  constructor(scene, stage) {
    this.stage = stage;
    this.pos = new Float32Array(MAX * 3);
    this.vel = new Float32Array(MAX * 3);
    this.life = new Float32Array(MAX);
    this.max = new Float32Array(MAX);
    this.size = new Float32Array(MAX);
    this.col = new Float32Array(MAX * 4);
    this.base = new Float32Array(MAX * 3);
    this.kind = new Uint8Array(MAX);  // 0 spark, 1 droplet, 2 mote, 3 riser, 4 twinkle
    this.next = 0;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('size', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('tint', new THREE.BufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage));
    this.mat = new THREE.ShaderMaterial({ vertexShader: vs, fragmentShader: fs, uniforms: { scale: { value: 300 } }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    this.points = new THREE.Points(geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
    scene.add(this.points);
    this.popups = [];
    this.layer = document.createElement('div');
    this.layer.className = 'fx-layer';
    document.body.appendChild(this.layer);
  }

  emit(kind, x, y, z, vx, vy, vz, life, size, color, alpha = 1) {
    const i = this.next; this.next = (this.next + 1) % MAX;
    this.pos.set([x, y, z], i * 3); this.vel.set([vx, vy, vz], i * 3);
    let { r, g, b } = color;
    // 影絵: no cyan or magenta light. Cool sparks become paper-white, pink ones amber;
    // the undertow's violet ink (dim) is left alone.
    if (this.kagee && r + g + b > 1.5) {
      if (b > r * 1.25 && g > r) { const k = (r + g + b) / 3.6; [r, g, b] = [1.5 * k, 1.35 * k, 1.05 * k]; }
      else if (r > g * 1.5 && b > g * 1.3) { [r, g, b] = [1.5, 0.85, 0.35]; }
    }
    this.base.set([r, g, b], i * 3);
    this.life[i] = life; this.max[i] = life; this.size[i] = size; this.kind[i] = kind;
    this.col[i * 4 + 3] = alpha;
  }

  // Contact sparks along a normal (nx, nz world), count by strength.
  sparks(x, z, nx, nz, strength, color = new THREE.Color(1.6, 1.4, 1.0)) {
    const n = Math.round(4 + strength * 10);
    for (let k = 0; k < n; k++) {
      const a = Math.atan2(nz, nx) + (Math.random() - 0.5) * 2.2;
      const s = 2 + Math.random() * 5 * (0.5 + strength);
      this.emit(0, x, 0.15, z, Math.cos(a) * s, 1 + Math.random() * 3, Math.sin(a) * s, 0.16 + Math.random() * 0.18, 4.5, color);
    }
  }
  splash(x, z, strength) {
    const n = Math.round(5 + strength * 12);
    for (let k = 0; k < n; k++) {
      const a = Math.random() * Math.PI * 2, s = 0.6 + Math.random() * 2.2 * (0.4 + strength);
      this.emit(1, x, 0.05, z, Math.cos(a) * s, 2.5 + Math.random() * 3.5 * strength, Math.sin(a) * s, 0.35 + Math.random() * 0.3, 4, new THREE.Color(0.7, 0.9, 1.1));
    }
  }
  // Plankton specks stirred in a wake; they linger and twinkle.
  plankton(x, z, tint, n = 2) {
    for (let k = 0; k < n; k++) {
      const c = new THREE.Color(0.35, 1.2, 1.5).lerp(tint, 0.25);
      this.emit(4, x + (Math.random() - 0.5) * 0.9, 0.03, z + (Math.random() - 0.5) * 0.9, (Math.random() - 0.5) * 0.2, 0.05, (Math.random() - 0.5) * 0.2, 0.6 + Math.random() * 0.8, 3 + Math.random() * 2, c, 0.8);
    }
  }
  // A saved little one becomes motes that rise toward the moon.
  rise(x, z, n = 14) {
    for (let k = 0; k < n; k++) {
      this.emit(3, x + (Math.random() - 0.5) * 0.6, 0.2 + Math.random() * 0.4, z + (Math.random() - 0.5) * 0.6, (Math.random() - 0.5) * 0.6, 1.4 + Math.random() * 1.6, -0.4 - Math.random() * 0.8, 1.4 + Math.random() * 1.0, 4 + Math.random() * 3, new THREE.Color(0.6, 1.4, 1.6));
    }
  }
  // A light pen: motes burst up from its cells.
  penBurst(points, strength) {
    const n = Math.min(points.length, 14 + Math.round(strength * 46));
    for (let k = 0; k < n; k++) {
      const [x, z] = points[Math.floor(Math.random() * points.length)];
      this.emit(3, x, 0.05, z, (Math.random() - 0.5) * 0.3, 0.5 + Math.random() * 1.3, (Math.random() - 0.5) * 0.3, 0.6 + Math.random() * 0.8, 3 + Math.random() * 2.5, new THREE.Color(0.45, 1.25, 1.45));
    }
  }
  // Rain over the pool: fast pale streaks that end in ripples.
  rain(cx, cz, w, d, n, water, now) {
    for (let k = 0; k < n; k++) {
      const x = cx + (Math.random() - 0.5) * w, z = cz + (Math.random() - 0.5) * d;
      // The 影絵 look draws rain as a paper overlay (style.css body.rain); only the rings land here.
      if (!water?.kagee) this.emit(5, x, 5 + Math.random() * 3, z, -0.8, -16, 0.3, 0.6, 3, new THREE.Color(0.55, 0.65, 0.85), 0.7);
      if (water && Math.random() < (water.kagee ? 0.25 : 0.08)) water.ripple(x, z, now, water.kagee ? 0.6 : 0.35);
    }
  }
  ink(x, z) {
    for (let k = 0; k < 10; k++) {
      const a = Math.random() * Math.PI * 2;
      this.emit(2, x, 0.05, z, Math.cos(a) * 0.5, 0.3, Math.sin(a) * 0.5, 0.9, 9, new THREE.Color(0.35, 0.18, 0.65), 0.7);
    }
  }

  popup(text, world, cls = '') {
    const el = document.createElement('div');
    el.className = 'popup ' + cls;
    el.textContent = text;
    this.layer.appendChild(el);
    this.popups.push({ el, world: world.clone(), t: 0 });
  }

  update(dt, camera, viewport) {
    // `size` is in CSS pixels at the pool's viewing distance.
    this.mat.uniforms.scale.value = (this.stage.look.focus || 30) * this.stage.renderer.getPixelRatio() * 0.5;
    for (let i = 0; i < MAX; i++) {
      if (this.life[i] <= 0) { this.col[i * 4 + 3] = 0; continue; }
      this.life[i] -= dt;
      const k = this.kind[i], j = i * 3;
      const t = this.life[i] / this.max[i];
      if (k === 1) this.vel[j + 1] -= 14 * dt;
      if (k === 0) { this.vel[j] *= 1 - 6 * dt; this.vel[j + 2] *= 1 - 6 * dt; this.vel[j + 1] -= 6 * dt; }
      if (k === 2 || k === 3) { this.vel[j] *= 1 - 1.5 * dt; this.vel[j + 2] *= 1 - 1.5 * dt; }
      this.pos[j] += this.vel[j] * dt; this.pos[j + 1] += this.vel[j + 1] * dt; this.pos[j + 2] += this.vel[j + 2] * dt;
      if ((k === 1 || k === 5) && this.pos[j + 1] < 0) this.life[i] = 0;
      let a = k === 4 ? (Math.sin((this.life[i] + i) * 9) > 0.2 ? 1 : 0.35) * Math.min(1, t * 3) : Math.min(1, t * 2);
      if (k === 3) a *= Math.min(1, (1 - t) * 6);
      this.col[i * 4] = this.base[j]; this.col[i * 4 + 1] = this.base[j + 1]; this.col[i * 4 + 2] = this.base[j + 2];
      this.col[i * 4 + 3] = a;
    }
    const g = this.points.geometry;
    g.attributes.position.needsUpdate = true; g.attributes.tint.needsUpdate = true; g.attributes.size.needsUpdate = true;
    for (const p of [...this.popups]) {
      p.t += dt;
      const v = p.world.clone().add(new THREE.Vector3(0, p.t * 0.9, 0)).project(camera);
      p.el.style.transform = `translate(${((v.x + 1) / 2) * viewport.w}px, ${((1 - v.y) / 2) * viewport.h}px) translate(-50%, -50%) scale(${p.t < 0.12 ? 0.6 + p.t * 4 : 1})`;
      p.el.style.opacity = String(Math.min(1, (1.4 - p.t) * 2));
      if (p.t > 1.4) { p.el.remove(); this.popups.splice(this.popups.indexOf(p), 1); }
    }
  }
}
